import React from 'react';
import { DiffHunk } from '../../types';
import { LineFoldControl } from './LineFoldControl';

interface SplitDiffViewProps {
  hunks: DiffHunk[];
  foldUnchanged: boolean;
  activeHunkIndex: number;
}

export const SplitDiffView: React.FC<SplitDiffViewProps> = ({
  hunks,
  foldUnchanged,
  activeHunkIndex,
}) => {
  return (
    <div className="w-full overflow-x-auto text-xs font-mono select-text">
      <table className="w-full border-collapse table-fixed min-w-[700px]">
        <colgroup>
          <col className="w-12 border-r border-slate-200 dark:border-slate-800" />
          <col className="w-1/2 border-r border-slate-200 dark:border-slate-800" />
          <col className="w-12 border-r border-slate-200 dark:border-slate-800" />
          <col className="w-1/2" />
        </colgroup>

        <thead className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 font-sans font-semibold text-[11px]">
          <tr>
            <th className="py-2.5 px-2 text-right">#</th>
            <th className="py-2.5 px-3 text-left">Previous Revision</th>
            <th className="py-2.5 px-2 text-right">#</th>
            <th className="py-2.5 px-3 text-left">New Revision</th>
          </tr>
        </thead>

        <tbody>
          {hunks.map((hunk, hIdx) => {
            const isUnchanged = hunk.changeType === 'unchanged' || hunk.change_type === 'unchanged';
            if (isUnchanged && foldUnchanged && hunk.lines.length > 8) {
              return (
                <LineFoldControl
                  key={`hunk-${hIdx}`}
                  lineCount={hunk.lines.length}
                  lines={hunk.lines}
                  mode="split"
                />
              );
            }

            const heading = hunk.sectionHeading || hunk.section_heading;

            return (
              <React.Fragment key={`hunk-${hIdx}`}>
                {/* Optional Section Heading Banner */}
                {heading && (
                  <tr className="bg-slate-100 dark:bg-slate-800/90 font-sans text-slate-700 dark:text-slate-300 font-semibold border-y border-slate-200 dark:border-slate-700">
                    <td colSpan={4} className="py-1.5 px-4 text-xs">
                      § {heading}
                    </td>
                  </tr>
                )}

                {/* Hunk Lines */}
                {hunk.lines.map((line, lIdx) => {
                  const isDel = line.type === '-' || line.type === 'deletion' || line.type === 'deleted';
                  const isAdd = line.type === '+' || line.type === 'addition' || line.type === 'added';
                  const wordTokens = line.word_tokens || line.wordTokens;
                  const oldNo = line.old_lineno || line.oldLineNumber;
                  const newNo = line.new_lineno || line.newLineNumber;

                  return (
                    <tr
                      key={`line-${hIdx}-${lIdx}`}
                      className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${
                        isDel
                          ? 'bg-rose-50/70 dark:bg-rose-950/20'
                          : isAdd
                          ? 'bg-emerald-50/70 dark:bg-emerald-950/20'
                          : ''
                      }`}
                    >
                      {/* Left Line Number */}
                      <td className="py-1 px-2 text-right text-slate-400 select-none border-r border-slate-200 dark:border-slate-800">
                        {oldNo || ''}
                      </td>

                      {/* Left Content (Deletion / Old) */}
                      <td className="py-1 px-3 whitespace-pre-wrap break-words border-r border-slate-200 dark:border-slate-800">
                        {isDel && wordTokens ? (
                          <span>
                            {wordTokens.map((wt, wIdx) => (
                              <span
                                key={wIdx}
                                className={
                                  wt.type === 'del' || wt.type === 'deleted'
                                    ? 'bg-rose-200 dark:bg-rose-900/60 text-rose-900 dark:text-rose-200 rounded px-0.5 font-semibold'
                                    : ''
                                }
                              >
                                {wt.text}
                              </span>
                            ))}
                          </span>
                        ) : isDel ? (
                          <span className="text-rose-900 dark:text-rose-300">{line.content}</span>
                        ) : isAdd ? (
                          <span className="text-slate-300 dark:text-slate-600"> </span>
                        ) : (
                          <span className="text-slate-700 dark:text-slate-300">{line.content}</span>
                        )}
                      </td>

                      {/* Right Line Number */}
                      <td className="py-1 px-2 text-right text-slate-400 select-none border-r border-slate-200 dark:border-slate-800">
                        {newNo || ''}
                      </td>

                      {/* Right Content (Addition / New) */}
                      <td className="py-1 px-3 whitespace-pre-wrap break-words">
                        {isAdd && wordTokens ? (
                          <span>
                            {wordTokens.map((wt, wIdx) => (
                              <span
                                key={wIdx}
                                className={
                                  wt.type === 'add' || wt.type === 'added'
                                    ? 'bg-emerald-200 dark:bg-emerald-900/60 text-emerald-900 dark:text-emerald-200 rounded px-0.5 font-semibold'
                                    : ''
                                }
                              >
                                {wt.text}
                              </span>
                            ))}
                          </span>
                        ) : isAdd ? (
                          <span className="text-emerald-900 dark:text-emerald-300">{line.content}</span>
                        ) : isDel ? (
                          <span className="text-slate-300 dark:text-slate-600"> </span>
                        ) : (
                          <span className="text-slate-700 dark:text-slate-300">{line.content}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
